import { StandardEvents } from '@shopify/events';

class DrWishlist extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.labels = JSON.parse(this.querySelector('[data-dr-wish-labels]').textContent);
    this.dialog = this.querySelector('dialog');
    this.list = this.querySelector('[data-dr-wish-items]');
    this.status = this.querySelector('[data-dr-wish-status]');
    this.feedback = this.querySelector('[data-dr-wish-feedback]');
    this.key = 'donreloj-favorites';
    this.cache = new Map();
    this.sequences = new WeakMap();
    this.toast = this.querySelector('[data-dr-wish-toast]');
    const options = { signal: this.abort.signal };
    document.addEventListener('click', event => {
      const button = event.target.closest('[data-dr-wish], [data-dr-wish-open], [data-dr-wish-remove], [data-dr-wish-close]');
      if (!button) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (button.hasAttribute('data-dr-wish')) this.toggle(button);
      else if (button.hasAttribute('data-dr-wish-open')) this.open(button);
      else if (button.hasAttribute('data-dr-wish-remove')) this.remove(button.dataset.drWishRemove);
      else this.dialog.close();
    }, { ...options, capture: true });
    this.dialog.addEventListener('click', event => { if (event.target === this.dialog) { const r = this.dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close(); } }, options);
    this.dialog.addEventListener('close', () => { this.fetchAbort?.abort(); this.opener?.focus(); }, options);
    window.addEventListener('storage', event => { if (event.key === this.key) { this.sync(); if (this.dialog.open) this.render(); } }, options);
    document.addEventListener('shopify:section:load', () => this.sync(), options);
    document.addEventListener(StandardEvents.productSelect, this.onProductSelect, options);
    this.observer = new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('[data-dr-wish],[data-dr-wish-open]') || node.querySelector?.('[data-dr-wish],[data-dr-wish-open]'))))) this.sync();
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
    this.sync();
  }
  validUrl(value) {
    try { const url = new URL(value, location.origin); return url.origin === location.origin && /\/products\//.test(url.pathname) ? url.pathname + url.search : ''; } catch { return ''; }
  }
  validImage(value) {
    if (!value) return '';
    try { const url = new URL(value, location.origin); return url.protocol === 'https:' && (url.origin === location.origin || url.hostname === 'cdn.shopify.com') ? url.href : ''; } catch { return ''; }
  }
  read() {
    try {
      const saved = JSON.parse(localStorage.getItem(this.key) || '[]');
      if (!Array.isArray(saved)) return [];
      return saved.filter(item => item && /^\d+$/.test(String(item.id)) && this.validUrl(item.url)).map(item => ({ id: String(item.id), title: String(item.title || ''), url: this.validUrl(item.url), image: this.validImage(item.image), price: String(item.price || ''), compare: String(item.compare || ''), variant: /^\d+$/.test(String(item.variant)) ? String(item.variant) : '', variantTitle: String(item.variantTitle || ''), unavailable: item.unavailable === true })).filter((item, i, all) => all.findIndex(other => other.id === item.id) === i).slice(0, 200);
    } catch { return []; }
  }
  write(items) {
    try { localStorage.setItem(this.key, JSON.stringify(items)); return true; }
    catch { this.announce(this.labels.storage_error); return false; }
  }
  announce(message) {
    this.status.textContent = message;
    if (this.dialog.open) this.feedback.textContent = message;
    else if (this.toast) {
      clearTimeout(this.toastTimer);
      this.toast.textContent = message;
      this.toast.hidden = false;
      this.toastTimer = setTimeout(() => { this.toast.hidden = true; }, 3200);
    }
  }
  onProductSelect = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const scope = target?.closest('product-card') || target?.closest('.shopify-section, dialog');
    if (!scope || !event.promise) return;
    const sequence = (this.sequences.get(scope) || 0) + 1;
    this.sequences.set(scope, sequence);
    scope.querySelectorAll('[data-dr-wish]').forEach(button => { button.disabled = true; });
    event.promise.then(({ detail }) => {
      if (!this.isConnected || this.sequences.get(scope) !== sequence) return;
      const fresh = detail?.html?.querySelector('[data-dr-wish]');
      if (!fresh || !detail?.resource?.id) return;
      scope.querySelectorAll('[data-dr-wish]').forEach(button => {
        if (button.dataset.drWish !== fresh.dataset.drWish) return;
        for (const key of ['wishUrl', 'wishTitle', 'wishImage', 'wishPrice', 'wishCompare', 'wishVariant', 'wishVariantTitle']) button.dataset[key] = fresh.dataset[key] || '';
        button.disabled = false;
      });
      this.sync();
    }).catch(() => {});
  };
  toggle(button) {
    const saved = this.read(), id = button.dataset.drWish;
    const variant = button.dataset.wishVariant || '';
    const exists = saved.some(item => item.id === id && (!item.variant || item.variant === variant));
    const next = saved.filter(item => item.id !== id);
    if (!exists) next.push({ id, variant, variantTitle: button.dataset.wishVariantTitle || '', title: button.dataset.wishTitle, url: button.dataset.wishUrl, image: button.dataset.wishImage, price: button.dataset.wishPrice, compare: button.dataset.wishCompare || '' });
    if (!this.write(next)) return;
    this.announce(`${exists ? this.labels.removed : this.labels.added}: ${button.dataset.wishTitle}${button.dataset.wishVariantTitle ? ' · ' + button.dataset.wishVariantTitle : ''}`);
    this.sync(); if (this.dialog.open) this.render();
  }
  remove(id) {
    if (!this.write(this.read().filter(item => item.id !== id))) return;
    this.sync(); this.render(); this.feedback.textContent = this.labels.removed;
    this.list.querySelector('button')?.focus();
  }
  sync() {
    const saved = this.read();
    document.querySelectorAll('[data-dr-wish]').forEach(button => {
      const active = saved.some(item => item.id === button.dataset.drWish && (!item.variant || item.variant === button.dataset.wishVariant));
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', `${active ? this.labels.remove : this.labels.save}: ${button.dataset.wishTitle}`);
      const label = button.querySelector('[data-dr-wish-label]'); if (label) label.textContent = active ? this.labels.saved : this.labels.save;
    });
    document.querySelectorAll('[data-dr-wish-count]').forEach(el => { el.textContent = String(saved.length); el.hidden = !saved.length; });
    document.querySelectorAll('[data-dr-wish-open]').forEach(el => el.setAttribute('aria-label', `${this.labels.title} (${saved.length})`));
  }
  open(button) {
    this.opener = button; this.feedback.textContent = ''; if (this.toast) this.toast.hidden = true; this.render();
    if (!this.dialog.open) this.dialog.showModal();
    this.refreshProducts();
  }
  render() {
    const saved = this.read();
    this.querySelector('[data-dr-wish-empty]').hidden = saved.length > 0;
    this.list.replaceChildren();
    for (const item of saved) {
      const row = document.createElement('article'); row.className = 'drw-item';
      const media = document.createElement('a'); media.href = item.url; media.className = 'drw-item-image'; media.tabIndex = -1; media.setAttribute('aria-hidden', 'true');
      if (item.image) { const img = document.createElement('img'); img.src = item.image; img.alt = ''; img.width = 110; img.height = 110; img.loading = 'lazy'; media.append(img); }
      const body = document.createElement('div'); const title = document.createElement('a'); title.href = item.url; title.textContent = item.title; title.className = 'drw-item-title';
      const price = document.createElement('p'); price.className = 'drw-item-price';
      const currentPrice = document.createElement('strong'); currentPrice.className = 'drw-current-price'; currentPrice.textContent = item.price;
      price.append(currentPrice);
      if (item.compare && item.compare !== item.price) {
        const comparePrice = document.createElement('s'); comparePrice.className = 'drw-compare-price'; comparePrice.textContent = item.compare;
        price.append(comparePrice);
      }
      const view = document.createElement('a'); view.href = item.url; view.className = 'drw-item-view'; view.textContent = this.labels.view + ' →';
      const remove = document.createElement('button'); remove.type = 'button'; remove.dataset.drWishRemove = item.id; remove.className = 'drw-item-remove'; remove.textContent = '×'; remove.setAttribute('aria-label', `${this.labels.remove}: ${item.title}`);
      const color = document.createElement('p'); color.className = 'drw-item-variant'; color.textContent = item.variantTitle; color.hidden = !item.variantTitle;
      if (item.unavailable) { price.replaceChildren(); price.textContent = this.labels.unavailable; }
      body.append(title, color, price, view); row.append(media, body, remove); this.list.append(row);
    }
  }
  async refreshProducts() {
    this.fetchAbort?.abort(); this.fetchAbort = new AbortController();
    const signal = this.fetchAbort.signal;
    const snapshots = this.read().slice(0, 40);
    const updates = [];
    const queue = [...snapshots];
    const worker = async () => {
      while (queue.length && !signal.aborted) {
        const item = queue.shift();
        try {
          const url = new URL(item.url, location.origin); url.pathname += '.js'; url.search = '';
          const cached = this.cache.get(url.href);
          let product = cached && Date.now() - cached.at < 60000 ? cached.product : null;
          if (!product) {
            const controller = new AbortController();
            const cancel = () => controller.abort();
            signal.addEventListener('abort', cancel, { once: true });
            const timeout = setTimeout(cancel, 8000);
            try {
              const response = await fetch(url, { signal: controller.signal });
              if (!response.ok) continue;
              product = await response.json();
            } finally {
              clearTimeout(timeout);
              signal.removeEventListener('abort', cancel);
            }
            if (String(product?.id) !== item.id) continue;
            if (this.cache.size >= 40) this.cache.delete(this.cache.keys().next().value);
            this.cache.set(url.href, { at: Date.now(), product });
          }
          if (signal.aborted || String(product.id) !== item.id) continue;
          const variants = Array.isArray(product.variants) ? product.variants : [];
          const variant = item.variant ? variants.find(v => String(v.id) === item.variant) : variants.find(v => v.available && v.price === product.price) || variants.find(v => v.available) || variants[0];
          if (!variant) { updates.push({ ...item, unavailable: true }); continue; }
          let price = item.price, compare = '';
          const cents = Number(variant.price), comparison = Number(variant.compare_at_price);
          if (Number.isFinite(cents) && this.dataset.currency) {
            const formatter = new Intl.NumberFormat(document.documentElement.lang || 'es', { style: 'currency', currency: this.dataset.currency });
            price = formatter.format(cents / 100);
            if (Number.isFinite(comparison) && comparison > cents) compare = formatter.format(comparison / 100);
          }
          const productUrl = new URL(item.url, location.origin); productUrl.searchParams.set('variant', String(variant.id));
          updates.push({ ...item, originalVariant: item.variant, variant: String(variant.id), variantTitle: variant.title === 'Default Title' ? '' : variant.title, title: product.title, url: productUrl.pathname + productUrl.search, image: this.validImage(variant.featured_image?.src || item.image || product.featured_image), price, compare, unavailable: !variant.available });
        } catch {}
      }
    };
    await Promise.all(Array.from({ length: Math.min(4, queue.length) }, worker));
    if (signal.aborted || !this.dialog.open) return;
    const fresh = this.read().map(item => {
      const update = updates.find(update => update.id === item.id && (update.originalVariant ?? update.variant) === item.variant);
      if (!update) return item;
      const { originalVariant, ...saved } = update;
      return saved;
    });
    if (this.write(fresh)) { this.render(); this.sync(); }
  }
  disconnectedCallback() { this.abort?.abort(); this.fetchAbort?.abort(); this.observer?.disconnect(); clearTimeout(this.toastTimer); }
}
if (!customElements.get('dr-wishlist')) customElements.define('dr-wishlist', DrWishlist);

